import type {
  AdminUser,
  Attachment,
  Card,
  CardType,
  Landing,
  Launch,
  LaunchType,
  NotificationItem,
  SessionUser,
  Task,
  TaskComment,
  TaskReactionEmoji,
  TaskRiskLevel,
  TaskStatus,
  TaskType,
  Template,
  UserRole,
  UserSettings,
  WorkspaceUser,
} from "../types/domain";
import { api } from "./client";

export const authApi = {
  me: () => api<{ user: SessionUser }>("/auth/me"),
};

export const settingsApi = {
  get: () => api<{ settings: UserSettings }>("/settings"),
  patch: (patch: Partial<UserSettings>) =>
    api<{ settings: UserSettings }>("/settings", { method: "PATCH", body: patch }),
};

export const typesApi = {
  list: () => api<{ types: CardType[] }>("/types"),
  create: (
    data: Omit<CardType, "id" | "isPreset" | "kind" | "sortOrder"> & {
      kind?: CardType["kind"];
    },
  ) => api<{ type: CardType }>("/types", { method: "POST", body: data }),
  patch: (id: string, patch: Partial<Omit<CardType, "id" | "isPreset">>) =>
    api<{ type: CardType }>(`/types/${id}`, { method: "PATCH", body: patch }),
  reorder: (ids: string[], opts?: { expectedOrder?: string[] }) =>
    api<{ types: CardType[] }>("/types/reorder", {
      method: "POST",
      body: opts?.expectedOrder ? { ids, __expectedOrder: opts.expectedOrder } : { ids },
    }),
  remove: (id: string) => api<void>(`/types/${id}`, { method: "DELETE" }),
};

export const launchTypesApi = {
  list: (includeArchived = false) =>
    api<{ launchTypes: LaunchType[] }>(
      `/launch-types${includeArchived ? "?includeArchived=1" : ""}`,
    ),
  get: (id: string) => api<{ launchType: LaunchType }>(`/launch-types/${id}`),
  create: (data: {
    name: string;
    description?: string | null;
    hint?: string | null;
    icon: string;
    color: string;
    typeSet?: "full" | "email" | "content" | "empty";
    sortOrder?: number;
  }) => api<{ launchType: LaunchType }>("/launch-types", { method: "POST", body: data }),
  patch: (
    id: string,
    patch: Partial<Omit<LaunchType, "id" | "isBuiltin" | "createdAt" | "updatedAt">>,
  ) => api<{ launchType: LaunchType }>(`/launch-types/${id}`, { method: "PATCH", body: patch }),
  remove: (id: string, opts?: { force?: boolean }) =>
    api<void>(`/launch-types/${id}${opts?.force ? "?force=1" : ""}`, {
      method: "DELETE",
    }),

  // Days inside a launch type
  addDay: (
    launchTypeId: string,
    data: { stage: string; position?: number; customLabel?: string | null },
  ) =>
    api<{ day: import("../types/domain").LaunchTypeDay }>(
      `/launch-types/${launchTypeId}/days`,
      { method: "POST", body: data },
    ),
  patchDay: (
    launchTypeId: string,
    dayKey: string,
    patch: { stage?: string; customLabel?: string | null },
  ) =>
    api<{ day: import("../types/domain").LaunchTypeDay }>(
      `/launch-types/${launchTypeId}/days/${dayKey}`,
      { method: "PATCH", body: patch },
    ),
  removeDay: (launchTypeId: string, dayKey: string) =>
    api<void>(`/launch-types/${launchTypeId}/days/${dayKey}`, { method: "DELETE" }),

  // Cards inside a launch type
  addCard: (
    launchTypeId: string,
    data: {
      dayKey: string;
      typeId: string;
      title: string;
      preview?: string;
      time?: string | null;
      position?: number;
    },
  ) =>
    api<{ card: import("../types/domain").LaunchTypeCard }>(
      `/launch-types/${launchTypeId}/cards`,
      { method: "POST", body: data },
    ),
  patchCard: (
    launchTypeId: string,
    cardId: string,
    patch: Partial<{
      dayKey: string;
      typeId: string;
      title: string;
      preview: string;
      time: string | null;
      position: number;
    }>,
    opts?: { expectedUpdatedAt?: string },
  ) =>
    api<{ card: import("../types/domain").LaunchTypeCard }>(
      `/launch-types/${launchTypeId}/cards/${cardId}`,
      {
        method: "PATCH",
        body: opts?.expectedUpdatedAt
          ? { ...patch, __expectedUpdatedAt: opts.expectedUpdatedAt }
          : patch,
      },
    ),
  moveCard: (
    launchTypeId: string,
    cardId: string,
    data: { dayKey: string; position: number },
  ) =>
    api<{ card: import("../types/domain").LaunchTypeCard }>(
      `/launch-types/${launchTypeId}/cards/${cardId}/move`,
      { method: "POST", body: data },
    ),
  duplicateCard: (launchTypeId: string, cardId: string) =>
    api<{ card: import("../types/domain").LaunchTypeCard }>(
      `/launch-types/${launchTypeId}/cards/${cardId}/duplicate`,
      { method: "POST" },
    ),
  removeCard: (launchTypeId: string, cardId: string) =>
    api<void>(`/launch-types/${launchTypeId}/cards/${cardId}`, { method: "DELETE" }),
};

export const launchesApi = {
  list: (status?: string) =>
    api<{ launches: Launch[] }>(`/launches${status ? `?status=${status}` : ""}`),
  get: (id: string) => api<{ launch: Launch }>(`/launches/${id}`),
  create: (data: {
    name: string;
    d1StartDate: string;
    days: Array<{ stage: string; customLabel?: string | null }>;
    emoji?: string;
    color?: string;
    goal?: string;
    audience?: string;
    description?: string;
    seedCards?: { typeSetKey: "full" | "email" | "content" | "empty" };
    launchTypeId?: string | null;
    status?: Launch["status"];
  }) => api<{ launch: Launch }>("/launches", { method: "POST", body: data }),
  patch: (id: string, patch: Partial<Launch>) =>
    api<{ launch: Launch }>(`/launches/${id}`, { method: "PATCH", body: patch }),
  remove: (id: string) => api<void>(`/launches/${id}`, { method: "DELETE" }),
  clone: (
    id: string,
    data: { name: string; d1StartDate: string; copyCards?: boolean; copyLandings?: boolean },
  ) => api<{ launch: Launch }>(`/launches/${id}/clone`, { method: "POST", body: data }),
  addDay: (id: string, data: { stage: string; position: number; customLabel?: string }) =>
    api<{ days: Launch["days"] }>(`/launches/${id}/days`, { method: "POST", body: data }),
  patchDay: (
    launchId: string,
    dayKey: string,
    patch: { stage?: string; customLabel?: string | null },
  ) =>
    api<{ days: Launch["days"] }>(`/launches/${launchId}/days/${dayKey}`, {
      method: "PATCH",
      body: patch,
    }),
  deleteDay: (launchId: string, dayKey: string, migrateCardsTo?: string) => {
    const q = migrateCardsTo ? `?migrateCardsTo=${encodeURIComponent(migrateCardsTo)}` : "";
    return api<{ days: Launch["days"] }>(`/launches/${launchId}/days/${dayKey}${q}`, {
      method: "DELETE",
    });
  },
};

export const cardsApi = {
  list: (launchId: string) => api<{ cards: Card[] }>(`/cards?launchId=${launchId}`),
  create: (data: Omit<Card, "id" | "createdAt" | "updatedAt" | "attachments" | "position" | "createdById"> & {
    position?: number;
  }) => api<{ card: Card }>("/cards", { method: "POST", body: data }),
  patch: (id: string, patch: Partial<Card>, opts?: { expectedUpdatedAt?: string }) =>
    api<{ card: Card }>(`/cards/${id}`, {
      method: "PATCH",
      body: opts?.expectedUpdatedAt
        ? { ...patch, __expectedUpdatedAt: opts.expectedUpdatedAt }
        : patch,
    }),
  duplicate: (id: string) => api<{ card: Card }>(`/cards/${id}/duplicate`, { method: "POST" }),
  move: (id: string, data: { dayKey: string; position: number }) =>
    api<{ card: Card }>(`/cards/${id}/move`, { method: "POST", body: data }),
  remove: (id: string) => api<void>(`/cards/${id}`, { method: "DELETE" }),
};

export const landingsApi = {
  list: (params?: { launchId?: string; status?: string }) => {
    const q = new URLSearchParams();
    if (params?.launchId) q.set("launchId", params.launchId);
    if (params?.status) q.set("status", params.status);
    const qs = q.toString();
    return api<{ landings: Landing[] }>(`/landings${qs ? `?${qs}` : ""}`);
  },
  get: (id: string) => api<{ landing: Landing }>(`/landings/${id}`),
  create: (data: Partial<Landing> & { title: string }) =>
    api<{ landing: Landing }>("/landings", { method: "POST", body: data }),
  patch: (id: string, patch: Partial<Landing>, opts?: { expectedUpdatedAt?: string }) =>
    api<{ landing: Landing }>(`/landings/${id}`, {
      method: "PATCH",
      body: opts?.expectedUpdatedAt
        ? { ...patch, __expectedUpdatedAt: opts.expectedUpdatedAt }
        : patch,
    }),
  remove: (id: string) => api<void>(`/landings/${id}`, { method: "DELETE" }),
};

export const templatesApi = {
  list: (params?: { stage?: string; tag?: string }) => {
    const q = new URLSearchParams();
    if (params?.stage) q.set("stage", params.stage);
    if (params?.tag) q.set("tag", params.tag);
    const qs = q.toString();
    return api<{ templates: Template[] }>(`/templates${qs ? `?${qs}` : ""}`);
  },
  create: (
    data: Omit<Template, "id" | "source" | "usageCount" | "lastUsedAt" | "createdAt" | "updatedAt">,
  ) => api<{ template: Template }>("/templates", { method: "POST", body: data }),
  patch: (id: string, patch: Partial<Template>) =>
    api<{ template: Template }>(`/templates/${id}`, { method: "PATCH", body: patch }),
  remove: (id: string) => api<void>(`/templates/${id}`, { method: "DELETE" }),
  apply: (id: string, data: { launchId: string; dayKey: string }) =>
    api<{ card: Card }>(`/templates/${id}/apply`, { method: "POST", body: data }),
};

export interface PresenceViewer {
  id: string;
  email: string;
  name: string | null;
  picture: string | null;
}

export const presenceApi = {
  heartbeatCard: (cardId: string) =>
    api<void>(`/presence/card/${cardId}/heartbeat`, { method: "POST" }),
  leaveCard: (cardId: string) =>
    api<void>(`/presence/card/${cardId}/leave`, { method: "POST" }),
  snapshotLaunch: (launchId: string) =>
    api<{ viewers: Record<string, PresenceViewer[]> }>(`/presence/launch/${launchId}`),
};

export interface TaskAuditActor {
  id: string;
  email: string;
  name: string | null;
  picture: string | null;
  role: UserRole;
}

export interface TaskAuditItem {
  id: string;
  action: "create" | "update" | "delete" | "move" | "clone" | "apply";
  diff: unknown;
  changedAt: string;
  taskId: string;
  /** null если задача уже удалена. */
  taskTitle: string | null;
  actor: TaskAuditActor | null;
}

export interface TaskAuditFilter {
  userId?: string;
  taskId?: string;
  action?: string;
  from?: string; // YYYY-MM-DD
  to?: string;
  q?: string;
  cursor?: string | null;
  limit?: number;
}

function buildQuery(p: Record<string, unknown>): string {
  const usp = new URLSearchParams();
  for (const [k, v] of Object.entries(p)) {
    if (v == null || v === "" || v === undefined) continue;
    usp.set(k, String(v));
  }
  const s = usp.toString();
  return s ? `?${s}` : "";
}

export const adminApi = {
  listUsers: () => api<{ users: AdminUser[] }>("/admin/users"),
  setRole: (id: string, role: UserRole) =>
    api<{ user: AdminUser }>(`/admin/users/${id}`, { method: "PATCH", body: { role } }),
  remove: (id: string) => api<void>(`/admin/users/${id}`, { method: "DELETE" }),
  taskAudit: (filter: TaskAuditFilter = {}) =>
    api<{ items: TaskAuditItem[]; nextCursor: string | null }>(
      `/admin/audit/tasks${buildQuery(filter as Record<string, unknown>)}`,
    ),

  // Roles & permissions
  listRoles: () =>
    api<{ roles: import("../types/domain").Role[] }>("/admin/roles"),
  permissionsSchema: () =>
    api<{ schema: import("../types/domain").PermissionSchemaItem[] }>(
      "/admin/permissions/schema",
    ),
  createRole: (data: {
    name: string;
    description?: string | null;
    permissions: import("../types/domain").Permissions;
    sortOrder?: number;
  }) =>
    api<{ role: import("../types/domain").Role }>("/admin/roles", {
      method: "POST",
      body: data,
    }),
  patchRole: (
    id: string,
    patch: Partial<{
      name: string;
      description: string | null;
      permissions: import("../types/domain").Permissions;
      sortOrder: number;
    }>,
  ) =>
    api<{ role: import("../types/domain").Role }>(`/admin/roles/${id}`, {
      method: "PATCH",
      body: patch,
    }),
  removeRole: (id: string) => api<void>(`/admin/roles/${id}`, { method: "DELETE" }),
};

export const attachmentsApi = {
  presignUpload: (data: { cardId: string; filename: string; mimeType: string; size: number }) =>
    api<{ uploadUrl: string; storageKey: string; expiresIn: number }>(
      "/attachments/presign-upload",
      { method: "POST", body: data },
    ),
  commit: (data: {
    cardId: string;
    storageKey: string;
    filename: string;
    mimeType: string;
    size: number;
  }) => api<{ attachment: Attachment }>("/attachments/commit", { method: "POST", body: data }),
  downloadUrl: (id: string) =>
    api<{ url: string; expiresIn: number; filename: string; mimeType: string }>(
      `/attachments/${id}/download-url`,
    ),
  remove: (id: string) => api<void>(`/attachments/${id}`, { method: "DELETE" }),
};

export interface TasksListParams {
  launchId?: string;
  from?: string;
  to?: string;
  includeUndated?: boolean;
  status?: TaskStatus;
  risk?: TaskRiskLevel;
  typeId?: string;
  assignee?: string;
}

export const taskTypesApi = {
  list: () => api<{ types: TaskType[] }>("/task-types"),
  create: (data: { name: string; color: string; bg: string; text: string }) =>
    api<{ type: TaskType }>("/task-types", { method: "POST", body: data }),
  patch: (id: string, patch: Partial<Omit<TaskType, "id" | "isPreset" | "createdAt">>) =>
    api<{ type: TaskType }>(`/task-types/${id}`, { method: "PATCH", body: patch }),
  reorder: (ids: string[], opts?: { expectedOrder?: string[] }) =>
    api<{ types: TaskType[] }>("/task-types/reorder", {
      method: "POST",
      body: opts?.expectedOrder ? { ids, __expectedOrder: opts.expectedOrder } : { ids },
    }),
  remove: (id: string) => api<void>(`/task-types/${id}`, { method: "DELETE" }),
};

export const tasksApi = {
  list: (params: TasksListParams = {}) => {
    const q = new URLSearchParams();
    if (params.launchId) q.set("launchId", params.launchId);
    if (params.from) q.set("from", params.from);
    if (params.to) q.set("to", params.to);
    if (params.includeUndated === false) q.set("includeUndated", "false");
    if (params.status) q.set("status", params.status);
    if (params.risk) q.set("risk", params.risk);
    if (params.typeId) q.set("typeId", params.typeId);
    if (params.assignee) q.set("assignee", params.assignee);
    const qs = q.toString();
    return api<{ tasks: Task[] }>(`/tasks${qs ? `?${qs}` : ""}`);
  },
  get: (id: string) => api<{ task: Task }>(`/tasks/${id}`),
  create: (data: {
    launchId?: string | null;
    title: string;
    description?: string | null;
    assigneeId?: string | null;
    assigneeName?: string | null;
    plannedDate?: string | null;
    dueDate?: string | null;
    dueTime?: string | null;
    status?: TaskStatus;
    riskLevel?: TaskRiskLevel;
    taskTypeId?: string;
    position?: number;
    recurrence?: {
      frequency: "weekly";
      weekDays: number[];
    } | null;
  }) => api<{ task: Task }>("/tasks", { method: "POST", body: data }),
  patch: (
    id: string,
    patch: Partial<{
      launchId: string | null;
      title: string;
      description: string | null;
      assigneeId: string | null;
      assigneeName: string | null;
      plannedDate: string | null;
      dueDate: string | null;
      dueTime: string | null;
      status: TaskStatus;
      riskLevel: TaskRiskLevel;
      taskTypeId: string;
      position: number;
    }>,
    opts?: { expectedUpdatedAt?: string },
  ) =>
    api<{ task: Task }>(`/tasks/${id}`, {
      method: "PATCH",
      body: opts?.expectedUpdatedAt
        ? { ...patch, __expectedUpdatedAt: opts.expectedUpdatedAt }
        : patch,
    }),
  move: (id: string, data: { plannedDate: string | null; position: number }) =>
    api<{ task: Task }>(`/tasks/${id}/move`, { method: "POST", body: data }),
  remove: (id: string, opts?: { scope?: "single" | "future" }) => {
    const q = opts?.scope === "future" ? "?scope=future" : "";
    return api<void>(`/tasks/${id}${q}`, { method: "DELETE" });
  },
  comments: {
    list: (taskId: string) =>
      api<{ comments: TaskComment[] }>(`/tasks/${taskId}/comments`),
    create: (taskId: string, body: string, mentionUserIds?: string[]) =>
      api<{ comment: TaskComment }>(`/tasks/${taskId}/comments`, {
        method: "POST",
        body: mentionUserIds?.length ? { body, mentionUserIds } : { body },
      }),
    patch: (taskId: string, commentId: string, body: string, mentionUserIds?: string[]) =>
      api<{ comment: TaskComment }>(`/tasks/${taskId}/comments/${commentId}`, {
        method: "PATCH",
        body: mentionUserIds?.length ? { body, mentionUserIds } : { body },
      }),
    remove: (taskId: string, commentId: string) =>
      api<void>(`/tasks/${taskId}/comments/${commentId}`, { method: "DELETE" }),
    addReaction: (taskId: string, commentId: string, emoji: TaskReactionEmoji) =>
      api<void>(`/tasks/${taskId}/comments/${commentId}/reactions`, {
        method: "POST",
        body: { emoji },
      }),
    removeReaction: (taskId: string, commentId: string, emoji: TaskReactionEmoji) =>
      api<void>(`/tasks/${taskId}/comments/${commentId}/reactions/${emoji}`, {
        method: "DELETE",
      }),
  },
};

export const usersApi = {
  list: () => api<{ users: WorkspaceUser[] }>("/users"),
};

export const notificationsApi = {
  list: (params: { unread?: boolean; limit?: number } = {}) => {
    const q = new URLSearchParams();
    if (params.unread) q.set("unread", "1");
    if (params.limit) q.set("limit", String(params.limit));
    const qs = q.toString();
    return api<{ notifications: NotificationItem[]; unreadCount: number }>(
      `/notifications${qs ? `?${qs}` : ""}`,
    );
  },
  markRead: (id: string) =>
    api<{ notification: NotificationItem }>(`/notifications/${id}/read`, {
      method: "POST",
    }),
  markAllRead: () => api<void>("/notifications/read-all", { method: "POST" }),
};

export const aiApi = {
  grammarCheck: (text: string, mode: "md" | "plain" = "md") =>
    api<{ corrected: string }>("/ai/grammar-check", {
      method: "POST",
      body: { text, mode },
    }),
};
